import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { DealItem, Fee, Offer } from '@psp-aggregator/sdk';

interface DealItemsProps {
  items: DealItem[];
}

const feeText = (fee: Fee | undefined): string => {
  if (!fee) {
    return '';
  }

  return fee.unit === 'percent' ? `${fee.value}%` : fee.value;
};

const offerLegs = (offer: Offer): { key: string; value: string }[] =>
  (
    [
      ['pay_in', offer.pay_in?.sell_rate],
      ['pay_out', offer.pay_out?.sell_rate],
      ['settlement', offer.settlement?.sell_rate],
      ['crypto_top_up', offer.crypto_top_up?.sell_rate],
    ] as const
  )
    .map(([key, fee]) => ({ key, value: feeText(fee) }))
    .filter((leg) => leg.value);

export const DealItems: FC<DealItemsProps> = ({ items }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('pages.admin.deals.ticket.noItems')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const conditions = [
          ['countries', item.offer.countries.join(', ')],
          ['currencies', item.offer.currencies.join(', ')],
        ].filter(([, value]) => value);

        return (
          <div
            key={item.id}
            className="rounded-xl border border-border bg-muted/30 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-foreground">{item.offer_alias}</p>
              <div className="flex flex-wrap gap-1">
                {offerLegs(item.offer).map((leg) => (
                  <span
                    key={leg.key}
                    className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary tabular-nums">
                    {leg.key} {leg.value}
                  </span>
                ))}
              </div>
            </div>

            {conditions.length > 0 && (
              <dl className="mt-3 grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
                {conditions.map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-muted-foreground">{key}</dt>
                    <dd className="truncate text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
};

DealItems.displayName = 'DealItems';
